import { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Lock, LogOut } from "lucide-react";
import { toast } from "sonner";
import { IMAGES } from "../data/products";
import { API, TOKEN_KEY, errMsg } from "./admin/shared";
import { AdminDashboard } from "./admin/AdminDashboard";
import { AdminOrders } from "./admin/AdminOrders";
import { AdminCustomers } from "./admin/AdminCustomers";
import { AdminProducts } from "./AdminProducts";

const TABS = [["dashboard", "Dashboard"], ["orders", "Orders"], ["products", "Products"], ["customers", "Customers"]];

const Login = ({ onDone }) => {
    const [pw, setPw] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/admin/login`, { password: pw });
            localStorage.setItem(TOKEN_KEY, data.token);
            onDone();
        } catch (err) {
            toast.error(errMsg(err, "Login failed"));
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="min-h-screen bg-ivory flex items-center justify-center px-6 pt-24" data-testid="admin-login-page">
            <form onSubmit={submit} className="w-full max-w-sm border border-gold/40 bg-cream/40 p-10">
                <img src={IMAGES.logo} alt="Al Zahra" className="w-16 mix-blend-multiply mb-8" />
                <p className="text-[10px] tracking-[0.3em] uppercase text-golddark mb-2">Maison Access</p>
                <h1 className="font-serif text-3xl text-charcoal mb-8">Admin Dashboard</h1>
                <label className="block mb-6">
                    <span className="text-[10px] tracking-[0.25em] uppercase text-ash block mb-1">Admin Password</span>
                    <input data-testid="admin-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus className="w-full bg-transparent border-b border-charcoal/20 focus:border-gold outline-none py-2 text-sm text-charcoal" />
                </label>
                <button data-testid="admin-login-button" disabled={busy || !pw} className="w-full flex items-center justify-center gap-2 bg-charcoal text-goldlight px-8 py-4 text-xs tracking-[0.25em] uppercase hover:bg-golddark hover:text-ivory transition-colors duration-300 disabled:opacity-50">
                    <Lock size={13} strokeWidth={1.5} /> {busy ? "Entering…" : "Enter"}
                </button>
            </form>
        </div>
    );
};

export default function AdminPage() {
    const nav = useNavigate();
    const { pathname } = useLocation();
    const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
    const [orderQuery, setOrderQuery] = useState("");
    const [openId, setOpenId] = useState(null);
    const tab = TABS.map(([k]) => k).find((k) => pathname.startsWith(`/admin/${k}`)) || "dashboard";
    const go = (k) => nav(k === "dashboard" ? "/admin" : `/admin/${k}`);

    const logout = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
    }, []);

    if (!authed) return <Login onDone={() => setAuthed(true)} />;

    const openOrder = (id) => { setOpenId(id); go("orders"); };
    const searchOrders = (q) => { setOrderQuery(q); go("orders"); };

    return (
        <div className="min-h-screen bg-ivory pt-28 pb-24 px-6 md:px-12" data-testid="admin-dashboard">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
                    <div>
                        <p className="text-[10px] tracking-[0.3em] uppercase text-golddark mb-2">Maison · Private</p>
                        <h1 className="font-serif text-4xl sm:text-5xl text-charcoal capitalize">{tab}</h1>
                        <div className="flex flex-wrap gap-2 mt-5" data-testid="admin-tabs">
                            {TABS.map(([k, label]) => (
                                <button key={k} data-testid={`admin-tab-${k}`} onClick={() => go(k)} className={`px-4 py-2 text-[10px] tracking-[0.25em] uppercase border transition-colors duration-300 ${tab === k ? "bg-charcoal text-goldlight border-charcoal" : "border-gold/30 text-smoke hover:border-gold"}`}>{label}</button>
                            ))}
                        </div>
                    </div>
                    <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-2 border border-charcoal/40 text-charcoal px-4 py-2.5 text-[10px] tracking-[0.2em] uppercase hover:bg-charcoal hover:text-goldlight transition-colors duration-300">
                        <LogOut size={12} /> Sign out
                    </button>
                </div>

                {tab === "dashboard" && <AdminDashboard onUnauthorized={logout} onOpenOrder={openOrder} />}
                {tab === "orders" && <AdminOrders onUnauthorized={logout} initialQuery={orderQuery} openId={openId} onOpenChange={setOpenId} />}
                {tab === "products" && <AdminProducts onUnauthorized={logout} />}
                {tab === "customers" && <AdminCustomers onUnauthorized={logout} onSearchOrders={searchOrders} />}
            </div>
        </div>
    );
}
